import { useState, useEffect } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useClasses, useDeleteClass } from "@/lib/api/classes/hooks";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit, Users } from "lucide-react";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { toast } from "sonner";
import { ClassDialog } from "@/components/classes/class-dialog";
import { Class } from "@/lib/api/classes/types";
import { apiClient } from "@/lib/api/client";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

interface UserProfile {
  _id: string;
  userId: string;
  name: string;
  role: string;
  isApproved: boolean;
  office?: {
    _id: string;
    name: string;
  };
}

function ClassesPage() {
  const navigate = useNavigate();
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const { data: classes, isLoading } = useClasses();
  const deleteClass = useDeleteClass();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await apiClient.get<UserProfile>("/auth/me");
        setUser(response.data);
      } catch {
        // Error handled by client interceptor
      }
    };
    loadUser();
  }, []);

  const handleCreate = () => {
    setEditingClass(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (classItem: Class) => {
    setEditingClass(classItem);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string, name: string) => {
    setAlertDialogState({
      title: "반 삭제",
      description: `정말 "${name}" 반을 삭제하시겠습니까?`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteClass.mutateAsync(id);
          toast.success("삭제되었습니다.");
        } catch {
          toast.error("삭제에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  const handleManageStudents = (classId: string) => {
    navigate({ to: `/classes/${classId}/students` });
  };

  const canModify = (classItem: Class) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return classItem.teacher._id === user._id;
  };

  const isAdmin = user?.role === "admin";

  useLoadingIndicator(isLoading);

  return (
    <PageContainer>
      <Header
        title="반 관리"
        right={
          <Button onClick={handleCreate}>
            <Plus className="size-4 mr-2" />새 반
          </Button>
        }
      />

      {/* 반 목록 */}
      <div className="bg-card rounded-lg border">
        {classes && classes.length > 0 ? (
          <>
            {/* 테이블 헤더 */}
            <div className="hidden md:grid grid-cols-6 gap-4 p-4 border-b font-semibold text-sm text-muted-foreground">
              <div>반 이름</div>
              <div>담당 선생님</div>
              <div>지점</div>
              <div>설명</div>
              <div>상태</div>
              <div></div>
            </div>

            {/* 테이블 바디 */}
            <div className="divide-y">
              {classes.map((classItem) => (
                <div
                  key={classItem._id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  {/* 데스크톱 뷰 */}
                  <div className="hidden md:grid grid-cols-6 gap-4 items-center">
                    <div className="font-medium">{classItem.name}</div>
                    <div className="text-sm">
                      {classItem.teacher?.name || "-"}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {classItem.office?.name || "-"}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {classItem.description || "-"}
                    </div>
                    <div>
                      <span
                        className={`inline-flex px-2 py-1 text-xs rounded ${
                          classItem.status === "active"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {classItem.status === "active" ? "활성" : "비활성"}
                      </span>
                    </div>
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleManageStudents(classItem._id)}
                        title="학생 관리"
                      >
                        <Users className="size-4" />
                      </Button>
                      {canModify(classItem) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(classItem)}
                        >
                          <Edit className="size-4" />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleDelete(classItem._id, classItem.name)
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* 모바일 뷰 */}
                  <div className="md:hidden space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{classItem.name}</span>
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs rounded ${
                            classItem.status === "active"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {classItem.status === "active" ? "활성" : "비활성"}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleManageStudents(classItem._id)}
                        >
                          <Users className="size-4" />
                        </Button>
                        {canModify(classItem) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(classItem)}
                          >
                            <Edit className="size-4" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleDelete(classItem._id, classItem.name)
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      담당: {classItem.teacher?.name || "-"}
                      {classItem.office?.name &&
                        ` · ${classItem.office.name}`}
                    </div>
                    {classItem.description && (
                      <div className="text-sm text-muted-foreground">
                        {classItem.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            등록된 반이 없습니다.
          </div>
        )}
      </div>

      {/* 생성/수정 다이얼로그 */}
      {user?.office && (
        <ClassDialog
          classData={editingClass}
          officeId={user.office._id}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
        />
      )}
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/classes/")({
  component: ClassesPage,
});
